import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
  useEffect,
} from "react";
import createTableTheme from "./tableTheme";
import { DESIGN_TOKENS, getDesignColors } from "./designTokens";
import { ThemeProvider as MUIThemeProvider, CssBaseline } from "@mui/material";

const ThemeModeContext = createContext(null);

export function ThemeModeProvider({ children }) {
  const [themeMode, setThemeMode] = useState(() => {
    try {
      return localStorage.getItem("themeMode") || "light";
    } catch {
      return "light";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("themeMode", themeMode);
    } catch {}
  }, [themeMode]);

  useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    if (!root) return;
    const colors = getDesignColors(themeMode);
    const { size, shape, motion, typography } = DESIGN_TOKENS;
    const vars = [
      ["--app-bg", colors.background],
      ["--app-bg-alt", colors.backgroundAlt],
      ["--app-paper", colors.paper],
      ["--app-elevated", colors.elevated],
      ["--app-tint", colors.tint],
      ["--app-tint-strong", colors.tintStrong],
      ["--app-text", colors.text],
      ["--app-text-secondary", colors.textSecondary],
      ["--app-text-tertiary", colors.textTertiary],
      ["--app-divider", colors.divider],
      ["--app-divider-soft", colors.dividerSoft],
      ["--app-primary", colors.primary],
      ["--app-primary-hover", colors.primaryHover],
      ["--app-primary-soft", colors.primarySoft],
      ["--app-primary-contrast", colors.primaryContrast],
      ["--app-link", colors.link],
      ["--app-accent", colors.accent],
      ["--app-error", colors.error],
      ["--app-input", colors.input],
      ["--app-glass", colors.glass],
      ["--app-glass-border", colors.glassBorder],
      ["--app-scrim", colors.scrim],
      ["--app-shadow-sm", colors.shadowSm],
      ["--app-shadow-md", colors.shadowMd],
      ["--app-shadow-lg", colors.shadowLg],
      ["--app-hero-gradient", colors.heroGradient],
      ["--app-surface-gradient", colors.surfaceGradient],
      ["--app-font", typography.fontFamily],
      ["--app-font-display", typography.displayFontFamily],
      ["--app-font-mono", typography.monoFontFamily],
      ["--app-ease", motion.easing],
      ["--app-ease-spring", motion.springEasing],
      ["--app-motion-fast", motion.fast],
      ["--app-motion-base", motion.base],
      ["--app-motion-slow", motion.slow],
      ["--table-head-bg", colors.tableHead],
      ["--table-stripe-bg", colors.tableStripe],
      ["--table-hover-bg", colors.tableHover],
      ["--table-mobile-card-bg", colors.paper],
      ["--table-mobile-label-color", colors.textSecondary],
      ["--table-mobile-border", colors.divider],
      ["--table-mobile-bg", colors.tableMobileBackground],
      ["--table-mobile-shadow", colors.shadowSm],
      ["--table-scrollbar", colors.tableScrollbar],
      ["--app-control-height", `${size.controlHeight}px`],
      ["--app-control-height-small", `${size.controlHeightSmall}px`],
      ["--app-button-height", `${size.buttonHeight}px`],
      ["--app-button-height-small", `${size.buttonHeightSmall}px`],
      ["--app-toolbar-gap", `${size.toolbarGap}px`],
      ["--app-icon-button-size", `${size.iconButton}px`],
      ["--app-filter-control-width", `${size.filterControlWidth}px`],
      ["--app-filter-select-width", `${size.filterSelectWidth}px`],
      ["--app-filter-autocomplete-width", `${size.filterAutocompleteWidth}px`],
      ["--app-filter-date-width", `${size.filterDateWidth}px`],
      ["--app-table-action-size", `${size.tableActionSize}px`],
      ["--app-table-action-icon-size", `${size.tableActionIconSize}px`],
      ["--app-content-max-width", `${size.contentMaxWidth}px`],
      ["--app-control-radius", `${shape.controlRadius}px`],
      ["--app-surface-radius", `${shape.surfaceRadius}px`],
      ["--app-dialog-radius", `${shape.dialogRadius}px`],
      ["--app-card-radius", `${shape.cardRadius}px`],
      ["--app-public-card-radius", `${shape.publicCardRadius}px`],
      ["--app-hero-radius", `${shape.heroRadius}px`],
      ["--app-pill-radius", `${shape.pillRadius}px`],
    ];
    root.dataset.theme = themeMode;
    root.style.colorScheme = themeMode;
    vars.forEach(([name, value]) => {
      try {
        root.style.setProperty(name, value);
      } catch {}
    });
  }, [themeMode]);

  // sync across tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "themeMode" && e.newValue) setThemeMode(e.newValue);
    };
    window.addEventListener && window.addEventListener("storage", onStorage);
    return () =>
      window.removeEventListener &&
      window.removeEventListener("storage", onStorage);
  }, []);

  const theme = createTableTheme(themeMode);

  return (
    <ThemeModeContext.Provider value={{ themeMode, setThemeMode }}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  const ctx = useContext(ThemeModeContext);
  if (!ctx)
    throw new Error("useThemeMode must be used inside ThemeModeProvider");
  return ctx;
}

export default ThemeModeContext;
